import { useMemo } from 'react'
import { useFormikContext } from 'formik'
import { useTranslation } from 'react-i18next'

import Alert from '@mui/material/Alert'
import Card from '@mui/material/Card'
import CardContent from '@mui/material/CardContent'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'

import SelectField from '@/components/formik/fields/SelectField'
import { useSearchFormTemplatesQuery } from '@/core/services/form'
import { FormTemplate } from '@/core/models/form.model'
import type { AvailabilityWizardValues } from '../CreateAvailabilityWizard'

export default function SecondOpinionReviewStep() {
  const { t } = useTranslation()
  const { values } = useFormikContext<AvailabilityWizardValues>()

  const { data: formTemplatesResponse } = useSearchFormTemplatesQuery({
    search: { isActive: true },
    skip: 0,
    limit: 100,
    orderBy: 'createdAt',
    order: 'DESC',
  })

  const formTemplateOptions = useMemo(() => {
    if (!formTemplatesResponse?.items) return []
    return formTemplatesResponse.items.map((template: FormTemplate) => ({
      label: template.title,
      value: template._id,
    }))
  }, [formTemplatesResponse])

  return (
    <Stack spacing={3}>
      <Stack spacing={0.5}>
        <Typography variant="h6">
          {t('page.meetingLink.wizard.secondOpinion.title', 'Review & Create')}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {t('page.meetingLink.wizard.secondOpinion.subtitle', 'Patients submit their case for your review — no scheduling needed')}
        </Typography>
      </Stack>

      <Alert severity="info" variant="outlined">
        {t('page.meetingLink.wizard.secondOpinion.info', 'Second opinions don\'t require a live meeting. Patients submit their files and you review on your own time.')}
      </Alert>

      <SelectField
        fullWidth
        label={t('field.formTemplate.label')}
        name="formTemplate"
        options={formTemplateOptions}
        id="formTemplate"
        inputProps={{ 'data-testid': 'availability-wizard-form-template' }}
      />

      <Card variant="outlined" sx={{ bgcolor: 'grey.50' }}>
        <CardContent>
          <Stack spacing={1}>
            <SummaryRow
              label={t('page.meetingLink.wizard.review.type', 'Type')}
              value={t('page.meetingLink.availabilitiesTable.cell.meetingPlace.secondOpinion')}
            />
            <SummaryRow
              label={t('field.speciality.label', { context: 'plural' })}
              value={values.specialities?.join(', ') || '—'}
            />
            <SummaryRow
              label={t('page.meetingLink.wizard.review.schedule', 'Schedule')}
              value={t('page.meetingLink.wizard.secondOpinion.alwaysOpen', 'Always open')}
            />
          </Stack>
        </CardContent>
      </Card>
    </Stack>
  )
}

function SummaryRow({ label, value }: { label: string; value: string }) {
  return (
    <Stack direction="row" justifyContent="space-between" sx={{ py: 0.5, borderBottom: '1px solid', borderColor: 'divider' }}>
      <Typography variant="body2" color="text.secondary">{label}</Typography>
      <Typography variant="body2" fontWeight={500}>{value}</Typography>
    </Stack>
  )
}
